import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Trash2, Image, Paperclip, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import '../ai/markdown-styles.css';

export interface Message {
  id: string;
  content: string;
  sender: 'user' | 'expert';
  timestamp: number;
  isLoading?: boolean;
}

interface MobileChatUIProps {
  messages?: Message[];
  onSendMessage: (message: string) => void;
  onClearChat?: () => void;
  onBack?: () => void;
  isLoading?: boolean;
  expertName?: string;
  expertTitle?: string;
  expertAvatar?: string;
  className?: string;
  suggestedQuestions?: string[];
  hideHeader?: boolean;
}

const MobileChatUI: React.FC<MobileChatUIProps> = ({
  messages = [],
  onSendMessage,
  onClearChat,
  onBack,
  isLoading = false,
  expertName = 'ChattyAI',
  expertTitle = 'Thiền sư AI',
  expertAvatar = 'https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public',
  className,
  suggestedQuestions = ['Thiền là gì?', 'Cách thực hành thiền tại nhà', 'Dạy tôi về Tứ Diệu Đế'],
  hideHeader = false
}) => {
  const { t } = useTranslation('ai');
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Cuộn xuống tin nhắn mới nhất khi có tin nhắn mới
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);
  
  // Kiểm tra nếu cần hiển thị nút cuộn xuống (chỉ khi có tin nhắn)
  useEffect(() => {
    if (messages.length === 0) {
      setShowScrollButton(false);
      return;
    }
    
    const checkScrollPosition = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isNearBottom);
      }
    };
    
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', checkScrollPosition);
      return () => chatContainer.removeEventListener('scroll', checkScrollPosition);
    }
  }, [messages.length]);
  
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };
  
  const handleSendMessage = () => {
    if (input && input.trim() && !isLoading) {
      onSendMessage(input);
      setInput('');
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const goBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };
  
  return (
    <div className={cn(
      "flex flex-col h-full min-h-[100dvh] bg-gray-50 dark:bg-gray-900 relative w-full overflow-hidden",
      className
    )}>
      {/* Header đầy đủ - chỉ hiển thị khi hideHeader=false */}
      {!hideHeader && (
        <div className="flex items-center p-4 bg-background border-b shadow-sm fixed top-0 left-0 right-0 z-50">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={goBack}
            className="mr-2 text-muted-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <Avatar className="h-8 w-8 mr-3">
            <AvatarImage src={expertAvatar || ''} alt={expertName || 'Expert'} />
            <AvatarFallback>{(expertName || 'AI').charAt(0)}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{expertName || 'Thiền Sư AI'}</div>
            <div className="text-xs text-muted-foreground truncate">{expertTitle || 'Thiền sư'}</div>
          </div>
          
          {onClearChat && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClearChat) onClearChat();
              }}
              className="ml-auto text-muted-foreground"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          )}
        </div>
      )}
      
      {/* Mini header - hiển thị khi hideHeader=true để không bị mất thông tin chuyên gia */}
      {hideHeader && (
        <div className="flex items-center py-1.5 px-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed top-0 left-0 right-0 z-50 shadow-xs min-h-[40px]">
          <Avatar className="h-6 w-6 mr-2">
            <AvatarImage src={expertAvatar || ''} alt={expertName || 'Expert'} />
            <AvatarFallback>{(expertName || 'AI').charAt(0)}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="text-sm font-semibold truncate">{expertName || 'Thiền Sư AI'}</div>
            <div className="text-xs text-muted-foreground truncate">{expertTitle || 'Thiền sư'}</div>
          </div>
          
          {onClearChat && (
            <Button
              variant="ghost" 
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClearChat) onClearChat();
              }}
              className="h-6 w-6 ml-1 text-muted-foreground p-0 z-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
      
      {/* Messages - sử dụng padding-top và padding-bottom để tạo khoảng trống cho header và input */}
      <div 
        ref={chatContainerRef}
        className={cn(
          "overflow-y-auto px-2 space-y-1.5 w-full",
          hideHeader ? "pt-12" : "pt-16", // padding-top tùy theo header
          messages.length === 0 ? "flex flex-col items-center justify-center px-4" : ""
        )}
        style={{
          position: "fixed",
          top: hideHeader ? "40px" : "60px",
          backgroundColor: "white",
          left: 0,
          right: 0,
          bottom: "125px", // Để lại khoảng trống cho phần input (55px) + bottom-nav (70px)
          overflowY: "auto",
          overscrollBehavior: "contain",
          paddingBottom: "16px"
        }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-8 text-center">
            <div className="w-20 h-20 mb-4">
              <img
                src={expertAvatar}
                alt={expertName}
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {t('welcome_to_chat', { name: expertName })} 👋
            </h2>
            <p className="text-muted-foreground mb-4">
              {t('start_chatting_prompt')}
            </p>
            <div className="grid grid-cols-1 gap-3 w-full">
              {suggestedQuestions.map((prompt) => (
                <Button
                  key={prompt}
                  variant="outline"
                  className="justify-start h-auto py-3 px-4 text-left"
                  onClick={() => onSendMessage(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            {messages.map((message, index) => (
              <div
                key={`msg-${message.id}-${index}`}
                className={cn(
                  "flex mt-1",
                  message.sender === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-full rounded-2xl px-4 py-3",
                    message.sender === 'user'
                      ? "bg-primary text-primary-foreground rounded-tr-none"
                      : "bg-muted rounded-tl-none"
                  )}
                >
                  {message.isLoading ? (
                    <div className="flex space-x-1 items-center px-2">
                      <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0s' }} />
                      <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0.4s' }} />
                    </div>
                  ) : (
                    <div 
                      dangerouslySetInnerHTML={{ __html: message.content }} 
                      className="message-content markdown-content" 
                      style={{ fontSize: '0.95rem', lineHeight: '1.4' }}
                    />
                  )}
                </div>
              </div>
            ))}
            {/* Thêm div rỗng có chiều cao đủ lớn để đảm bảo tin nhắn cuối không bị che khuất */}
            <div ref={messagesEndRef} className="h-8 w-full" />
          </div>
        )}
      </div>
      
      {/* Scroll to bottom button - điều chỉnh vị trí để không bị che bởi input */}
      {showScrollButton && (
        <Button
          size="icon"
          variant="secondary"
          className="fixed bottom-[130px] right-4 rounded-full shadow-md z-50"
          onClick={scrollToBottom}
        >
          <ChevronLeft className="h-5 w-5 rotate-90" />
        </Button>
      )}
      
      {/* Input area - cố định ở dưới, trên thanh navigation */}
      <div className="p-3 bg-background border-t fixed bottom-[70px] left-0 right-0 z-50 w-full">
        <div className="flex items-center gap-2 w-full">
          <div className="flex-1 relative w-full"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('type_message')}
              disabled={isLoading}
              className="pr-12 py-5 bg-muted/50 w-full"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                disabled={isLoading}
              >
                <Paperclip className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <Button
            disabled={!input || !input.trim() || isLoading}
            onClick={handleSendMessage}
            size="icon"
            className="h-11 w-11 rounded-full bg-primary"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MobileChatUI; 